import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useUserBookingQuery } from "../../api/bookingApi.ts";
import { AlertCircle } from "lucide-react";
import { usePaymentMutation } from "../../api/checkoutApi.ts";
// Use the "/pure" entry so Stripe.js is only injected when loadStripe() is
// actually called (at checkout), not eagerly on import. Keeps us within the
// TDDDG strictly-necessary cookie exemption — see privacy review.
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { Trans, useLingui } from "@lingui/react/macro";

const Checkout: React.FC = () => {
  const { t } = useLingui();
  const { bookingId } = useParams<{ bookingId: string }>();
  const stripePromise = useMemo(
    () =>
      loadStripe(
        "pk_test_51RvJN5EMcGEEF390QA3fvBFBIdZcfVddd6Trs1wvxPrJ42rHyUVyeu0lFuFkgglv3fmX3L9g5cgW07pzmUJL9UUK005rMJu1za",
      ),
    [],
  );
  const hasInitiatedPayment = useRef(false);

  // Only make the booking query if bookingId exists
  const {
    data: booking,
    isError: bookingError,
    isLoading: bookingLoading,
  } = useUserBookingQuery(bookingId!, {
    enabled: !!bookingId,
  });

  const {
    mutate: postPayment,
    data: createPaymentResponse,
    isPending: paymentPending,
    isError: paymentError,
  } = usePaymentMutation();

  useEffect(() => {
    if (booking && bookingId && !hasInitiatedPayment.current) {
      hasInitiatedPayment.current = true;
      postPayment(bookingId);
    }
  }, [booking, bookingId, postPayment]);

  // Handle missing bookingId
  if (!bookingId) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="font-medium text-red-800">
              <Trans>
                Invalid booking ID. Please check your link and try again.
              </Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (bookingLoading || paymentPending) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-4 h-12 w-12">
              <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4"></div>
              <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent"></div>
            </div>
            <p className="text-gray-600">
              {bookingLoading
                ? t`Loading booking...`
                : t`Setting up payment...`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (bookingError || !booking || paymentError) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">
                {paymentError
                  ? t`Error setting up payment`
                  : t`Error loading booking or payment details`}
              </p>
              <p className="mt-1 text-sm text-red-700">
                <Trans>
                  Please try again or contact support if the problem persists.
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!createPaymentResponse?.sessionSecret) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-4 h-12 w-12">
              <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4"></div>
              <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent"></div>
            </div>
            <p className="text-gray-600">
              <Trans>Preparing checkout</Trans>...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret: createPaymentResponse.sessionSecret,
  };

  return (
    <div className="bg-white md:pt-2">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default Checkout;
